import { Plan, BillingPeriod } from "@/types/plans";
import { useState } from "react";
import { useAsaasCheckout } from "@/hooks/useAsaasCheckout";
import { PaymentMethodModal } from "./PaymentMethodModal";
import { debugLogger } from "@/utils/debugLogger";
import { PremiumBenefitsDisplay } from "./PremiumBenefitsDisplay";
import { PlanSelectionGrid } from "./PlanSelectionGrid";
import { SubscribeButton } from "./SubscribeButton";
import { getPlanCommercialName, centsToReais } from "@/constants/plans";

interface SubscriptionsComparisonTableProps {
  plans: Plan[];
  currentPlan: string;
  onUpgrade: (planId: string, billingPeriod: BillingPeriod) => void;
  calculateSavings: (prices: any) => any;
  isLoading: boolean;
  userRole?: string;
}

export const SubscriptionsComparisonTable = ({
  plans,
  currentPlan,
  onUpgrade,
  calculateSavings,
  isLoading,
  userRole
}: SubscriptionsComparisonTableProps) => {
  const { createCheckoutSession, isLoading: asaasLoading, paymentModal } = useAsaasCheckout();
  
  // Estado para seleção global (plano + período) - Pré-selecionar Infinity mensal dinamicamente por role
  const getDefaultPlanId = () => {
    const premiumPlan = plans.find(p => p.id.includes('premium'));
    return premiumPlan?.id || 'sponsor_premium';
  };

  const [selectedOption, setSelectedOption] = useState<{
    planId: string;
    period: BillingPeriod;
  } | null>({
    planId: getDefaultPlanId(),
    period: 'monthly'
  });

  const handleSelectionChange = (selection: { planId: string; period: BillingPeriod } | null) => {
    debugLogger.debug('SubscriptionsComparisonTable', 'Selection changed:', selection);
    setSelectedOption(selection);
  };

  // Determinar se um plano é pago
  const isPaidPlan = (planId: string) => {
    return planId === 'sponsor_standard' || planId === 'sponsor_premium' || planId === 'sponsored_premium';
  };

  // Função para lidar com assinatura
  const handleSubscribe = async () => {
    if (!selectedOption) return;
    
    const { planId, period } = selectedOption;
    if (currentPlan === planId) return;

    debugLogger.debug('SubscriptionsComparisonTable', 'Subscribing to:', { planId, period });

    if (isPaidPlan(planId)) {
      // Planos pagos usam Asaas
      try {
        await createCheckoutSession(planId, period);
      } catch (error) {
        debugLogger.error('SubscriptionsComparisonTable', 'Erro no checkout:', error);
      }
    } else {
      // Planos gratuitos
      onUpgrade(planId, period);
    }
  };

  // Filtrar planos para evitar downgrades
  const getFilteredPlans = () => {
    // Se usuário tem plano pago, não mostrar gratuito
    if (isPaidPlan(currentPlan)) {
      const filteredPlans = plans.filter(plan => plan.id !== 'sponsor_free' && plan.id !== 'sponsored_free');
      
      // Se usuário é sponsor_standard, mostrar apenas premium
      if (currentPlan === 'sponsor_standard') {
        return filteredPlans.filter(plan => plan.id === 'sponsor_premium');
      }
      
      // Se usuário é sponsor_premium, não mostrar outros planos
      if (currentPlan === 'sponsor_premium') {
        return [];
      }
      
      return filteredPlans;
    }
    
    return plans;
  };

  // Ordenar planos: Premium primeiro
  const sortedPlans = [...getFilteredPlans()].sort((a, b) => {
    if (userRole === 'sponsor') {
      const order = { sponsor_premium: 0, sponsor_standard: 1, sponsor_free: 2 };
      return (order as any)[a.id] - (order as any)[b.id];
    } else {
      const order = { sponsored_premium: 0, sponsored_free: 1 };
      return (order as any)[a.id] - (order as any)[b.id];
    }
  });

  // Se usuário tem plano premium e não há planos para mostrar, exibir benefícios
  if (sortedPlans.length === 0 && (currentPlan === 'sponsor_premium' || currentPlan === 'sponsored_premium')) {
    return <PremiumBenefitsDisplay userRole={userRole} />;
  }

  // Filtrar planos disponíveis
  const availablePlans = sortedPlans;

  // Get selected plan info for button
  const getSelectedPlanInfo = () => {
    if (!selectedOption) return { name: "", price: 0 };
    const plan = availablePlans.find(p => p.id === selectedOption.planId);
    if (!plan) return { name: "", price: 0 };
    
    const periodPrices = {
      weekly: plan.prices.weekly,
      monthly: plan.prices.monthly,
      quarterly: plan.prices.quarterly,
    };
    
    return {
      name: getPlanCommercialName(plan.id),
      price: periodPrices[selectedOption.period],
    };
  };

  const selectedPlanInfo = getSelectedPlanInfo();

  return (
    <div className="space-y-6">
      {/* Payment Method Collection Modal for Asaas */}
      <PaymentMethodModal
        isOpen={paymentModal.isOpen}
        onClose={paymentModal.onClose}
        onPixGenerate={paymentModal.onPixGenerate}
        onPaymentMethodSelect={paymentModal.onPaymentMethodSelect}
        planName={paymentModal.planName}
        planValue={paymentModal.planValue}
        isLoading={paymentModal.isLoading}
        pixData={paymentModal.pixData}
      />

      {/* Plan Selection Grid (responsive) */}
      <PlanSelectionGrid
        plans={availablePlans}
        currentPlan={currentPlan}
        userRole={userRole}
        onSelectionChange={handleSelectionChange}
        initialSelection={{ 
          planId: availablePlans.find(p => p.id.includes('premium'))?.id || 'sponsor_premium', 
          period: 'monthly' 
        }}
      />

      {/* Subscribe Button - handles own positioning */}
      <SubscribeButton
        selectedPlan={selectedOption?.planId || null}
        selectedPeriod={selectedOption?.period || null}
        planName={selectedPlanInfo.name}
        price={selectedPlanInfo.price}
        currentPlan={currentPlan}
        isLoading={isLoading || asaasLoading}
        onSubscribe={handleSubscribe}
      />
    </div>
  );
};
